<template>
  <!-- Pending: one blocking prompt standing in for N mutating calls gated in a
       single assistant round. Same warning-tinted grammar as ToolApprovalCard,
       scaled up to a checklist. -->
  <div v-if="!settled" class="approval-card my-2 pa-3">
    <div class="d-flex align-center ga-2 mb-1">
      <v-icon icon="mdi-shield-alert-outline" color="warning" size="small" />
      <span class="text-body-2 font-weight-medium">
        {{ t('approval.requiredCount', { count: message.expectedCount }) }}
      </span>
      <!-- Items stream in just after the envelope (milliseconds); show an
           indeterminate spinner until the list is complete rather than block. -->
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="16"
        width="2"
        color="warning"
        class="ml-1"
      />
    </div>

    <div v-if="message.headline" class="text-body-2 text-medium-emphasis mb-2">
      {{ message.headline }}
    </div>

    <div class="batch-items mb-3">
      <div v-for="item in visibleItems" :key="item.toolCallId" class="batch-item">
        <div class="d-flex align-center ga-2">
          <!-- Pending item: an interactive checkbox (default checked). Already
               settled out-of-band (e.g. a second tab used the per-call POST):
               show its decision, disabled, and exclude it from the submit. -->
          <v-checkbox-btn
            v-if="item.status === 'pending'"
            :model-value="isChecked(item)"
            color="success"
            density="compact"
            :disabled="loading || submitting"
            class="batch-check"
            @update:model-value="(v) => toggleItem(item.toolCallId, v)"
          />
          <v-icon
            v-else
            :icon="itemStatusIcon(item)"
            :color="itemStatusColor(item)"
            size="small"
            class="batch-status"
          />
          <span
            class="batch-label text-body-2 flex-grow-1"
            :class="{ 'text-medium-emphasis': item.status !== 'pending' }"
          >{{ itemLabel(item) }}</span>
          <v-btn
            v-if="rowsFor(item).length"
            :icon="isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            size="x-small"
            variant="text"
            density="comfortable"
            @click="toggleExpand(item.toolCallId)"
          />
        </div>

        <!-- Expanded: the same argument detail rows the single card shows. -->
        <div v-if="isExpanded(item) && rowsFor(item).length" class="approval-details mt-1 mb-2">
          <div
            v-for="(row, i) in rowsFor(item)"
            :key="i"
            class="approval-row text-body-2"
            :class="{ 'text-medium-emphasis': row.muted }"
            :style="{ paddingLeft: `${row.indent * 16}px` }"
          >
            <span v-if="row.label" class="row-label">{{ row.label }}</span>
            <span class="row-value">{{ row.value }}</span>
          </div>
        </div>
      </div>

      <!-- A round can legally gate up to 150 calls; cap the initial list. -->
      <v-btn
        v-if="!showAll && hiddenCount > 0"
        variant="text"
        size="small"
        class="mt-1"
        @click="showAll = true"
      >
        {{ t('approval.andMore', { count: hiddenCount }) }}
      </v-btn>
    </div>

    <div class="d-flex ga-2">
      <v-btn
        size="small"
        color="success"
        variant="flat"
        prepend-icon="mdi-check"
        :disabled="loading || submitting || pendingItems.length === 0"
        :loading="submitting"
        @click="submit(true)"
      >
        {{ approveLabel }}
      </v-btn>
      <v-btn
        size="small"
        color="error"
        variant="outlined"
        prepend-icon="mdi-close"
        :disabled="loading || submitting || pendingItems.length === 0"
        @click="submit(false)"
      >
        {{ t('approval.denyAll') }}
      </v-btn>
    </div>
  </div>

  <!-- Decided: collapse to a compact status chip, counts derived from items. -->
  <div v-else class="my-2">
    <v-chip size="small" :color="chipColor" variant="tonal">
      <v-icon :icon="chipIcon" size="x-small" start />
      {{ settledSummary }}
    </v-chip>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, reactive, computed } from 'vue'
import { useChat } from '../../composables/useChat.js'
import { buildApprovalRows } from '../../utils/approvalSummary.js'

const props = defineProps({
  message: { type: Object, required: true },
})

const { approveBatch } = useChat()

const VISIBLE_LIMIT = 20

const submitting = ref(false)
const showAll = ref(false)
// Which item detail panels are open, keyed by toolCallId.
const expanded = reactive({})
// Which items the user has UN-checked, keyed by toolCallId. Tracking the
// unchecked set (rather than the checked one) means items default to checked
// with no per-item initialisation as they stream in.
const uncheckedMap = reactive({})

// Still receiving the per-call events for this round.
const loading = computed(() => props.message.items.length < props.message.expectedCount)
// The batch row flips to 'settled' once every item is decided (see the store).
const settled = computed(() => props.message.status !== 'pending')

const visibleItems = computed(() =>
  showAll.value ? props.message.items : props.message.items.slice(0, VISIBLE_LIMIT)
)
const hiddenCount = computed(() => Math.max(0, props.message.items.length - VISIBLE_LIMIT))

const pendingItems = computed(() => props.message.items.filter((it) => it.status === 'pending'))
const selectedCount = computed(() =>
  pendingItems.value.reduce((n, it) => n + (isChecked(it) ? 1 : 0), 0)
)
const approveLabel = computed(() =>
  selectedCount.value === pendingItems.value.length
    ? t('approval.approveAll')
    : t('approval.approveSelected', { count: selectedCount.value })
)

function isChecked(item) {
  return !uncheckedMap[item.toolCallId]
}
function toggleItem(toolCallId, value) {
  if (value) delete uncheckedMap[toolCallId]
  else uncheckedMap[toolCallId] = true
}

function isExpanded(item) {
  return !!expanded[item.toolCallId]
}
function toggleExpand(toolCallId) {
  expanded[toolCallId] = !expanded[toolCallId]
}

// Argument detail rows, same as the single card. Memoised per item — the
// arguments never change once set, and a batch can carry many items.
const rowsCache = new Map()
function rowsFor(item) {
  let rows = rowsCache.get(item.toolCallId)
  if (!rows) {
    rows = buildApprovalRows(item.arguments, item.fieldTitles)
    rowsCache.set(item.toolCallId, rows)
  }
  return rows
}

// Model intent, else the first resolved title (e.g. the location's name), else
// the raw tool name.
function itemLabel(item) {
  if (item.summary) return item.summary
  const titles = item.fieldTitles ? Object.values(item.fieldTitles) : []
  if (titles.length) return titles[0]
  return item.name
}

function itemStatusIcon(item) {
  if (item.status === 'denied') return 'mdi-close'
  if (item.status === 'timeout') return 'mdi-timer-off-outline'
  return 'mdi-check'
}
function itemStatusColor(item) {
  if (item.status === 'denied') return 'error'
  if (item.status === 'timeout') return 'warning'
  return 'success'
}

const counts = computed(() => {
  let approved = 0
  let denied = 0
  let timedOut = 0
  for (const it of props.message.items) {
    if (it.status === 'denied') denied++
    else if (it.status === 'timeout') timedOut++
    // Any other settled decision (approved, or a future 'auto_approved').
    else if (it.status !== 'pending') approved++
  }
  return { approved, denied, timedOut, total: props.message.items.length }
})

const settledSummary = computed(() => {
  const { approved, denied, timedOut, total } = counts.value
  if (total > 0 && timedOut === total) return t('approval.timedOut')
  const parts = []
  if (approved) parts.push(t('approval.countApproved', { count: approved }))
  if (denied) parts.push(t('approval.countDenied', { count: denied }))
  if (timedOut) parts.push(t('approval.countTimedOut', { count: timedOut }))
  return parts.join(' · ') || t('approval.settled')
})

const chipColor = computed(() => {
  const { approved, denied, timedOut, total } = counts.value
  if (total > 0 && timedOut === total) return 'warning'
  if (denied === 0 && timedOut === 0) return 'success'
  if (approved === 0 && denied > 0) return 'error'
  return 'warning'
})
const chipIcon = computed(() => {
  const { approved, denied, timedOut, total } = counts.value
  if (total > 0 && timedOut === total) return 'mdi-timer-off-outline'
  if (denied === 0 && timedOut === 0) return 'mdi-check'
  if (approved === 0 && denied > 0) return 'mdi-close'
  return 'mdi-check'
})

async function submit(approveChecked) {
  // Send a decision for EVERY still-pending item so no gate is left to time
  // out. Deny all forces every decision false; Approve honours the checkboxes
  // (unchecked ⇒ denied). Items settled out-of-band are already excluded.
  const decisions = props.message.items
    .filter((it) => it.status === 'pending')
    .map((it) => ({ toolCallId: it.toolCallId, approved: approveChecked ? isChecked(it) : false }))
  if (decisions.length === 0) return

  submitting.value = true
  try {
    await approveBatch(props.message.batchId, decisions)
  } finally {
    // On success the card has collapsed to the settled chip; on failure
    // approveBatch rolled the items back to pending, so clearing this re-enables
    // the buttons for a retry.
    submitting.value = false
  }
}
</script>

<style scoped>
.approval-card {
  max-width: 85%;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-warning), 0.5);
  background: rgba(var(--v-theme-warning), 0.05);
}
.batch-item {
  border-top: 1px solid rgba(var(--v-theme-warning), 0.15);
  padding: 2px 0;
}
.batch-item:first-child {
  border-top: none;
}
.batch-check {
  flex: 0 0 auto;
}
.batch-status {
  width: 32px;
  text-align: center;
  flex: 0 0 auto;
}
.batch-label {
  overflow-wrap: anywhere;
}
.approval-details {
  border-left: 2px solid rgba(var(--v-theme-warning), 0.3);
  padding: 4px 0 4px 10px;
}
.approval-row {
  display: flex;
  gap: 8px;
  line-height: 1.6;
}
.row-label {
  flex-shrink: 0;
  opacity: 0.65;
}
.row-label::after {
  content: ':';
}
.row-value {
  overflow-wrap: anywhere;
}
</style>
